import { useEffect, useState, useMemo } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getTasksPaged,
  getTaskFormData,
  createTask,
  updateTask,
  getTaskById,
  deleteTask,
} from '../../../../../services/taskSaasServices';
import TaskCreateEditModal from './TaskCreateEditModal';
import ConfirmModal from '../../../../../components/ConfirmModal/ConfirmModal';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configTasksI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';

const normalizeId = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? value : numeric;
};

const mapTask = (task) => ({
  id: task.id,
  order: task.order || 0,
  code: task.code || '',
  name: task.name || '',
  serviceId: normalizeId(task.serviceId ?? task.servicesId),
  serviceName: task.serviceName || '-',
  documentId: normalizeId(task.documentId),
  documentName: task.documentName || '',
  businessAreaId: normalizeId(task.businessAreaId),
  taskTypeId: normalizeId(task.taskTypeId),
  taskTypeName: task.taskTypeName || '',
  startMessage: task.startMessage || '',
  laborCost: task.laborCost ?? 0,
  endMessage: task.endMessage || '-',
  description: task.description || '',
});

export default function ConfigTasks() {
  const { language } = useLanguage();
  const tasksLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configTasksI18n });
  const { showSnackbar } = useSnackbar();

  const location = useLocation();
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Button, Input, Table, IconButton, Modal, Dropdown, Combobox } =
    DesignSystemComponents ?? {};

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isLoadingSaving, setIsLoadingSaving] = useState(false);

  const [services, setServices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  const [serviceFilter, setServiceFilter] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [formValue, setFormValue] = useState({
    id: null,
    order: '',
    code: '',
    description: '',
    serviceId: null,
    serviceName: '',
    documentId: null,
    taskTypeId: null,
    startMessage: '',
    laborCost: 0,
    endMessage: '',
    businessAreaId: null,
  });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const [searchDebounced, setSearchDebounced] = useState('');
  const stateSearch = location.state?.search ?? '';

  useEffect(() => {
    configTasksI18n.changeLanguage(tasksLanguage);
  }, [tasksLanguage]);

  useEffect(() => {
    if (stateSearch) {
      setSearch(stateSearch);
      setPage(0);
    }
  }, [stateSearch]);

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'order':
        return 'order';
      case 'code':
        return 'code';
      case 'description':
        return 'description';
      case 'serviceName':
        return 'serviceName';
      case 'taskTypeName':
        return 'taskTypeName';
      case 'laborCost':
        return 'laborCost';
      case 'document':
        return 'documentName';
      default:
        return null;
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'order';
  const backendSortDesc =
    sortDirection === null ? false : sortDirection === 'desc';

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let mounted = true;

    const fetchFormData = async () => {
      try {
        setLoadingServices(true);
        const response = await getTaskFormData();

        if (!mounted) return;

        setServices(response.services || []);
        setDocuments(response.documents || []);
        setTaskTypes(response.taskTypes || []);
      } catch (error) {
        console.error('Error loading task form data:', error);
      } finally {
        if (mounted) {
          setLoadingServices(false);
        }
      }
    };

    fetchFormData();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchTasks = async () => {
      try {
        setLoading(true);

        const backendPage = page + 1;

        const response = await getTasksPaged(
          backendPage,
          pageSize,
          searchDebounced || null,
          'Activos',
          true,
          true,
          backendSortKey,
          backendSortDesc
        );

        if (!mounted) return;

        if (Array.isArray(response.documents) && response.documents.length) {
          setDocuments(response.documents);
        }

        const mappedTasks = response.tasks.map(mapTask);

        setRows(mappedTasks);
        setTotal(response.total);
      } catch (error) {
        console.error('Error loading tasks:', error);
        if (mounted) {
          showSnackbar(error.customMessage || t('config_tasks_error_load'), 'error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchTasks();

    return () => {
      mounted = false;
    };
  }, [
    page,
    pageSize,
    searchDebounced,
    backendSortKey,
    backendSortDesc,
    showSnackbar,
    t,
  ]);

  const serviceFilterOptions = useMemo(() => {
    const options = [
      { label: t('config_tasks_filter_all_services'), value: '__ALL__' },
    ];
    services.forEach((service) => {
      options.push({
        label: service.name,
        value: service.id,
      });
    });
    return options;
  }, [services, t]);

  const serviceModalOptions = useMemo(() => {
    return services.map((service) => ({
      label: service.name,
      value: service.id,
    }));
  }, [services]);

  const documentModalOptions = useMemo(() => {
    return documents.map((doc) => ({
      label: doc.name,
      value: normalizeId(doc.id),
    }));
  }, [documents]);

  const documentNameById = useMemo(() => {
    const lookup = {};
    documents.forEach((doc) => {
      const normalizedId = normalizeId(doc?.id);
      if (normalizedId !== null) {
        lookup[normalizedId] = doc.name;
      }
    });
    return lookup;
  }, [documents]);

  const taskTypeModalOptions = useMemo(() => {
    return taskTypes.map((type) => ({
      label: type.name,
      value: normalizeId(type.id),
    }));
  }, [taskTypes]);

  const taskTypeNameById = useMemo(() => {
    const lookup = {};
    taskTypes.forEach((type) => {
      const normalizedId = normalizeId(type?.id);
      if (normalizedId !== null) {
        lookup[normalizedId] = type.name;
      }
    });
    return lookup;
  }, [taskTypes]);

  const filtered = useMemo(() => {
    const serviceVal =
      serviceFilter?.value && serviceFilter.value !== '__ALL__'
        ? serviceFilter.value
        : null;

    if (!serviceVal) return rows;

    return rows.filter((row) => row.serviceId === serviceVal);
  }, [rows, serviceFilter]);

  const openCreate = () => {
    setMode('create');
    setFormValue({
      id: null,
      order: '',
      code: '',
      description: '',
      serviceId: null,
      serviceName: '',
      documentId: null,
      taskTypeId: null,
      startMessage: '',
      laborCost: 0,
      endMessage: '',
      businessAreaId: null,
    });
    setIsFormOpen(true);
  };

  const openEdit = async (row) => {
    try {
      setLoadingServices(true);

      const response = await getTaskById(row.id);
      setServices(response.services || []);
      if (Array.isArray(response.documents)) {
        setDocuments(response.documents);
      }
      setTaskTypes(response.taskTypes || []);

      setMode('edit');
      setFormValue({
        id: response.task.id,
        order: response.task.order,
        code: response.task.code,
        description: response.task.description,
        serviceId: normalizeId(
          response.task.servicesId ?? response.task.serviceId
        ),
        serviceName: response.task.serviceName,
        documentId: normalizeId(
          response.task.documentSendId ?? response.task.documentId
        ),
        taskTypeId: normalizeId(response.task.taskTypeId),
        startMessage: response.task.startMessage || '',
        laborCost: response.task.laborCost || 0,
        endMessage: response.task.endMessage || '',
        businessAreaId: normalizeId(response.task.businessAreaId),
      });

      setIsFormOpen(true);
    } catch (error) {
      console.error('Error loading task:', error);
      showSnackbar(t('config_tasks_error_load_single'), 'error');
    } finally {
      setLoadingServices(false);
    }
  };

  const openDelete = (row) => {
    setToDelete(row);
    setIsDeleteOpen(true);
  };

  const saveForm = async (updatedValue) => {
    const dataToSave = updatedValue || formValue;

    if (
      !dataToSave.description.trim() ||
      !dataToSave.code.trim() ||
      !dataToSave.serviceId ||
      !dataToSave.taskTypeId ||
      String(dataToSave.order).trim() === ''
    ) {
      showSnackbar(t('config_tasks_required_fields'), 'error');
      return;
    }

    try {
      setIsLoadingSaving(true);
      if (mode === 'create') {
        const response = await createTask({
          order: Number(dataToSave.order),
          code: dataToSave.code,
          description: dataToSave.description,
          serviceId: dataToSave.serviceId,
          documentSendId: dataToSave.documentId,
          taskTypeId: dataToSave.taskTypeId,
          startMessage: dataToSave.startMessage,
          laborCost: dataToSave.laborCost,
          endMessage: dataToSave.endMessage,
          businessAreaId: dataToSave.businessAreaId,
        });

        showSnackbar(t('config_tasks_success_created'), 'success');

        const newTask = {
          id: response.id,
          order: Number(dataToSave.order),
          code: dataToSave.code,
          description: dataToSave.description,
          serviceId: dataToSave.serviceId,
          serviceName:
            services.find((service) => service.id === dataToSave.serviceId)
              ?.name || '',
          documentId: dataToSave.documentId,
          documentName: documentNameById[dataToSave.documentId] || '',
          taskTypeId: dataToSave.taskTypeId,
          taskTypeName: taskTypeNameById[dataToSave.taskTypeId] || '',
          startMessage: dataToSave.startMessage,
          laborCost: dataToSave.laborCost,
          endMessage: dataToSave.endMessage,
          businessAreaId: dataToSave.businessAreaId,
        };
        setRows((prevRows) => [...prevRows, newTask]);
        setTotal((prevTotal) => prevTotal + 1);
      } else {
        await updateTask(dataToSave.id, {
          order: Number(dataToSave.order),
          code: dataToSave.code,
          description: dataToSave.description,
          serviceId: dataToSave.serviceId,
          documentSendId: dataToSave.documentId,
          taskTypeId: dataToSave.taskTypeId,
          startMessage: dataToSave.startMessage,
          laborCost: dataToSave.laborCost,
          endMessage: dataToSave.endMessage,
          businessAreaId: dataToSave.businessAreaId,
        });

        showSnackbar(t('config_tasks_success_updated'), 'success');

        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === dataToSave.id
              ? {
                  ...row,
                  order: Number(dataToSave.order),
                  code: dataToSave.code,
                  description: dataToSave.description,
                  serviceId: dataToSave.serviceId,
                  serviceName:
                    services.find((service) => service.id === dataToSave.serviceId)
                      ?.name || row.serviceName,
                  documentId: dataToSave.documentId,
                  documentName:
                    documentNameById[dataToSave.documentId] || row.documentName,
                  taskTypeId: dataToSave.taskTypeId,
                  taskTypeName:
                    taskTypeNameById[dataToSave.taskTypeId] || row.taskTypeName,
                  startMessage: dataToSave.startMessage,
                  laborCost: dataToSave.laborCost,
                  endMessage: dataToSave.endMessage,
                  businessAreaId: dataToSave.businessAreaId,
                }
              : row
          )
        );
      }
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error saving task:', error);
      showSnackbar(error.customMessage || t('config_tasks_error_save'), 'error');
    } finally {
      setIsLoadingSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete?.id) return;

    try {
      await deleteTask(toDelete.id);

      setIsDeleteOpen(false);
      setToDelete(null);

      showSnackbar(t('config_tasks_success_deleted'), 'success');

      const backendPage = page + 1;
      const response = await getTasksPaged(
        backendPage,
        pageSize,
        searchDebounced || null,
        'Activos',
        true,
        true,
        backendSortKey,
        backendSortDesc
      );

      if (Array.isArray(response.documents) && response.documents.length) {
        setDocuments(response.documents);
      }

      const mappedTasks = response.tasks.map(mapTask);

      setRows(mappedTasks);
      setTotal(response.total);
    } catch (error) {
      console.error('Error deleting task:', error);

      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        error.customMessage ||
        t('config_tasks_error_delete');

      showSnackbar(errorMessage, 'error');
    }
  };

  if (!Button || !Input || !Table || !IconButton || !Modal || !Dropdown) {
    return null;
  }

  return (
    <div>
      <div className='row mb-3'>
        <div className='col-6'>
          <Input
            icon='search'
            name='searchTask'
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('config_tasks_search_placeholder')}
            type='text'
            value={search}
          />
        </div>
        <div className='col-4'>
          <Dropdown
            options={serviceFilterOptions}
            value={serviceFilter ?? serviceFilterOptions[0]}
            onChange={(option) => setServiceFilter(option)}
            placeholder={t('config_tasks_filter_all_services')}
            width='100%'
            isDisabled={loadingServices}
          />
        </div>
        <div className='col-2'>
          <div className='d-flex justify-content-end'>
            <Button
              label={t('config_tasks_add_button')}
              size='sm'
              variant='primary'
              onClick={openCreate}
            />
          </div>
        </div>
      </div>

      <div className='table-container'>
        <Table
          columns={[
            {
              accessor: 'order',
              label: t('config_tasks_col_order'),
              width: '80px',
              sort: true,
            },
            {
              accessor: 'code',
              label: t('config_tasks_col_code'),
              width: '160px',
              sort: true,
            },
            {
              accessor: 'description',
              label: t('config_tasks_col_description'),
              sort: true,
            },
            {
              accessor: 'serviceName',
              label: t('config_tasks_col_service'),
              width: '16%',
              sort: true,
            },
            {
              accessor: 'taskTypeName',
              label: t('config_tasks_col_type'),
              width: '120px',
              sort: true,
              render: (row) => {
                const name =
                  row.taskTypeName || taskTypeNameById[row.taskTypeId] || '-';

                return <div className='color-gray-600'>{name}</div>;
              },
            },
            {
              accessor: 'laborCost',
              label: t('config_tasks_col_weight'),
              width: '80px',
              sort: true,
              render: (row) => (
                <div className='color-gray-600'>{row.laborCost}</div>
              ),
            },
            {
              accessor: 'document',
              label: t('config_tasks_col_document'),
              width: '16%',
              sort: true,
              render: (row) => (
                <div className='color-gray-600'>
                  {documentNameById[row.documentId] || row.documentName || '-'}
                </div>
              ),
            },
            {
              label: t('config_tasks_col_actions'),
              align: 'right',
              width: '120px',
              render: (row) => (
                <div className='d-flex gap-2'>
                  <IconButton
                    ariaLabel={t('config_tasks_action_edit')}
                    icon='pencil'
                    onClick={() => openEdit(row)}
                    size='md'
                    iconStroke='var(--primary-500)'
                  />
                  <IconButton
                    ariaLabel={t('config_tasks_action_delete')}
                    icon='trash'
                    onClick={() => openDelete(row)}
                    size='md'
                    iconStroke='var(--error-500)'
                  />
                </div>
              ),
            },
          ]}
          data={filtered}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          paginationMode='server'
          totalItems={total}
          paginator
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          loading={loading}
          serverSortKey={sortKey}
          serverSortDirection={sortDirection}
          onServerSortChange={handleServerSortChange}
        />
        {!loading && filtered.length === 0 && (
          <div className='text-center p-4'>
            <p className='caption font-light'>{t('config_tasks_no_results')}</p>
          </div>
        )}
      </div>

      <TaskCreateEditModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        Combobox={Combobox}
        DesignSystemComponents={DesignSystemComponents}
        show={isFormOpen}
        mode={mode}
        value={formValue}
        onChange={setFormValue}
        onClose={() => setIsFormOpen(false)}
        onSave={saveForm}
        serviceOptions={serviceModalOptions}
        documentOptions={documentModalOptions}
        taskTypeOptions={taskTypeModalOptions}
        loadingServices={loadingServices}
        onRequestDelete={
          mode === 'edit'
            ? () => {
                setIsFormOpen(false);
                setToDelete(formValue);
                setIsDeleteOpen(true);
              }
            : undefined
        }
        isLoadingSaving={isLoadingSaving}
      />

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('config_tasks_confirm_delete_title')}
        message={
          toDelete
            ? t('config_tasks_confirm_delete_message', { code: toDelete.code })
            : t('config_tasks_confirm_delete_fallback')
        }
        confirmLabel={t('config_tasks_confirm_delete_button')}
        cancelLabel={t('config_tasks_cancel')}
        confirmVariant='danger'
        onConfirm={confirmDelete}
        onClose={() => {
          setIsDeleteOpen(false);
          setToDelete(null);
        }}
      />
    </div>
  );
}
